// Performance V2 — the shared "Performance Over Time" chart (#4051, #4185,
// #4255). Metric delta-cards double as multi-metric line toggles; amber dots
// overlay Glitch optimizations and slate dots user edits (#4255); weekend
// columns shade on daily grouping; a dotted previous-period line renders when
// Compare-to is on (#4293) with an explicit failure notice (#4950).
// Extracted from performance-v2.tsx (#4932 Phase 0.4 follow-up); no behavior
// change. All series data is computed in the shell (chartData feeds the
// delta cards, spend trends and totals too) and arrives as props.
import { TrendingUp, TrendingDown, AlertCircle, Check, Info } from 'lucide-react';
import {
  Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot,
} from 'recharts';

import type { ActionLog } from '@/lib/types';
import type { ActivityEvent } from '@/lib/apis';
import { Card } from '@/components/ui/card';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PlatformLogo } from '@/lib/performance/platform';
import type { Platform } from '@/lib/performance/types';
import { fmt, EmptyChart } from './cells';

// The surface's canonical metric order (ruling 09-08): Cost, Impressions,
// Clicks, Conversions, CPA, CPC, ROAS — tiles, legend, chart lines and the
// hover box all derive from this list.
const METRIC_OPTIONS = [
  { value: 'spend', label: 'Cost', group: 'Volume' },
  { value: 'impressions', label: 'Impressions', group: 'Volume' },
  { value: 'clicks', label: 'Clicks', group: 'Volume' },
  { value: 'conversions', label: 'Conversions', group: 'Volume' },
  // Meta's per-day leads, merged into the chart data by the consolidated
  // wrapper (never present on the Google-only perf-v2 surface).
  { value: 'metaLeads', label: 'Leads', group: 'Volume' },
  { value: 'cpa', label: 'CPA', group: 'Efficiency' },
  { value: 'cpc', label: 'CPC', group: 'Efficiency' },
  { value: 'roas', label: 'ROAS', group: 'Efficiency' },
  { value: 'conversionValue', label: 'Conv. Value', group: 'Efficiency' },
];

const METRIC_COLORS: Record<string, string> = {
  spend: '#290BD9', impressions: '#059669', clicks: '#0891b2',
  conversions: '#ea580c', metaLeads: '#d946ef', cpc: '#7c3aed', cpa: '#be185d',
  roas: '#0d9488', conversionValue: '#4f46e5',
};

// One row per bucket (day/week/month): the base metrics plus prev_* keys when
// Compare-to is on, and the _weekendBg flag that drives weekend shading.
export type ChartDatum = { date: string } & { [key: string]: number | string | undefined };

export interface DeltaCard {
  key: string;
  label: string;
  kind: 'sum' | 'derived';
  current: number;
  previous: number;
  delta: number;
  hasComparison: boolean;
  improving: boolean;
  isCurrency: boolean;
  lowerIsBetter: boolean;
  /** A derived ratio whose denominator is zero in the window: rendered as a
      dash, never a fake 0 (€0.00 CPA reads as free conversions; 0.0x ROAS
      as no return when values simply are not tracked). */
  unmeasured?: boolean;
  unmeasuredNote?: string;
  /** Cross-platform display override (the blended Overview row): headline
      text plus per-platform sub-lines. The numeric fields and deltas stay
      the hook's Google math; only the rendering is overridden. */
  displayValue?: string;
  small?: boolean;
  subLines?: { platform: Platform; text: string }[];
  /** Hover disclosure for the whole tile (e.g. the conversion-reporting lag
      on Total Conversions when the window touches recent days). */
  tooltip?: string;
}

export function PerformanceChart({
  chartData,
  chartGroupBy,
  selectedMetric,
  selectedMetrics,
  toggleMetric,
  deltaCards,
  actionDatesOnChart,
  userEditDatesOnChart,
  showAiActions,
  onToggleAiActions,
  showUserEdits,
  onToggleUserEdits,
  comparisonMode,
  timeSeriesError,
  previousTimeSeriesError,
  hasWeekendDays,
  currencySymbol,
  hideYAxis = false,
  onRetry,
  footnotes,
  emptyMessage,
}: {
  chartData: ChartDatum[];
  chartGroupBy: 'day' | 'week' | 'month';
  selectedMetric: string;
  selectedMetrics: Set<string>;
  toggleMetric: (key: string) => void;
  deltaCards: DeltaCard[];
  actionDatesOnChart: { date: string; value: number; count: number; actions: ActionLog[] }[];
  userEditDatesOnChart: { date: string; value: number; count: number; events: ActivityEvent[] }[];
  showAiActions: boolean;
  onToggleAiActions: () => void;
  showUserEdits: boolean;
  onToggleUserEdits: () => void;
  comparisonMode: string;
  timeSeriesError: Error | null;
  previousTimeSeriesError: Error | null;
  hasWeekendDays: boolean;
  currencySymbol: string;
  /** #6499 — the consolidated Overview renders the chart x-axis-only. */
  hideYAxis?: boolean;
  /** Refetch the series; rendered as a Retry button inside the error state. */
  onRetry?: () => void;
  /** Data-caveat disclosures (conversion lag, Meta sync gap) under the tiles. */
  footnotes?: string[];
  /** Overrides the no-data copy — e.g. "Create a campaign to see performance
      here." when the org has no campaigns at all (nothing was measured, vs
      the measured-zero flat line when campaigns exist). */
  emptyMessage?: string;
}) {
  const cs = currencySymbol;

  return (
    <Card className="p-4 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-4 pr-7">
        <h4 className="text-sm font-semibold text-gray-900">Performance Over Time</h4>
        {/* #4255 — Independent toggle pills for the two overlay series.
            Optimizations = amber (Glitch ActionLog). User edits = slate
            (Google Ads change_event rows). Each pill is only rendered when
            its source has dots to show, avoiding empty-state controls. */}
        <div className="flex items-center gap-1.5">
          {actionDatesOnChart.length > 0 && (
            <button
              type="button"
              onClick={onToggleAiActions}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors',
                showAiActions
                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              )}
            >
              <div className={cn('w-2 h-2 rounded-full', showAiActions ? 'bg-amber-500' : 'bg-gray-300')} />
              Optimizations ({actionDatesOnChart.reduce((s, a) => s + a.count, 0)})
            </button>
          )}
          {userEditDatesOnChart.length > 0 && (
            <button
              type="button"
              onClick={onToggleUserEdits}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors',
                showUserEdits
                  ? 'bg-slate-100 border-slate-300 text-slate-700'
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              )}
            >
              <div className={cn('w-2 h-2 rounded-full', showUserEdits ? 'bg-slate-500' : 'bg-gray-300')} />
              User edits ({userEditDatesOnChart.reduce((s, a) => s + a.count, 0)})
            </button>
          )}
        </div>
      </div>
      {/* #4950 — a failed comparison fetch must not silently drop the dotted
          previous-period overlay: say so instead of looking like "no change". */}
      {comparisonMode !== 'none' && previousTimeSeriesError && !timeSeriesError && (
        <div className="mb-3 flex items-start gap-2 rounded-md border-l-4 border-[#FCA5A5] bg-[#FEE2E2] px-4 py-3 text-sm text-[#B91C1C]">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p>Previous-period comparison data failed to load, so the compare overlay and deltas are hidden. Try refreshing the page.</p>
        </div>
      )}
      {/* Chart. #4255 — renders one <Line> per metric in selectedMetrics so
          users can layer Clicks + Conversions + Spend etc. Dotted compare
          overlay only renders when exactly one metric is selected (multiple
          dotted previous-period lines on top of multiple solid lines is
          unreadable). The Tooltip is a custom renderer that lists every
          plotted metric for the hovered date and surfaces any AI actions
          that landed that day. */}
      {/* #4950 — error-first: a failed time-series fetch must not render as the
          "no performance data yet" empty state (users read that as zero
          performance). Surface the failure explicitly. */}
      {/* The chart body holds a constant 280px in every state — data, empty
          or error — so the page never jumps between them; the error state
          carries its Retry inline. */}
      {timeSeriesError ? (
        <div className="flex h-[280px] flex-col items-center justify-center text-center">
          <AlertCircle className="mb-3 h-10 w-10 text-[#FCA5A5]" strokeWidth={1} />
          <p className="text-sm font-medium text-[#B91C1C]">Performance data failed to load</p>
          <p className="mt-1 text-sm text-gray-400">This is a loading error, not zero performance.</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-3 rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Retry
            </button>
          )}
        </div>
      ) : chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={280}>
          {/* Horizontal margins keep the first and last tick labels inside
              the plot: with the y-axis hidden there is no left gutter, and
              edge labels centre on the plot boundary and clip without one. */}
          <LineChart data={chartData} margin={{ top: 4, right: 18, bottom: 0, left: hideYAxis ? 18 : 4 }}>
            <CartesianGrid vertical={false} stroke="#f3f4f6" />
            {/* Tilt the labels -30° so 30 daily ticks fit without Recharts'
                auto-thinning dropping every other one. `interval={0}` forces
                every tick to render; the angle + textAnchor='end' anchors the
                rotated text to its tick. `tickMargin` adds breathing room
                between the axis line and the rotated label so dates don't
                sit flush against the chart bottom. */}
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              interval={chartGroupBy === 'day' && chartData.length > 14 ? 'preserveStartEnd' : 0}
              angle={chartGroupBy === 'day' && chartData.length > 14 ? -30 : 0}
              textAnchor={chartGroupBy === 'day' && chartData.length > 14 ? 'end' : 'middle'}
              height={chartGroupBy === 'day' && chartData.length > 14 ? 60 : 36}
              tickMargin={chartGroupBy === 'day' && chartData.length > 14 ? 12 : 8}
              minTickGap={chartGroupBy === 'day' ? 2 : 5}
            />
            <YAxis hide={hideYAxis} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            {/* Weekend column shading — rendered as full-height Bars on a
                hidden secondary Y-axis with domain [0,1] so each weekend
                day's value of 1 fills the column. Bars are declared before
                Lines so they sit under the data. Lives on its own yAxisId
                so the primary axis scale stays metric-driven. */}
            {hasWeekendDays && (
              <>
                <YAxis yAxisId="weekend" hide domain={[0, 1]} />
                <Bar
                  yAxisId="weekend"
                  dataKey="_weekendBg"
                  fill="#94a3b8"
                  fillOpacity={0.1}
                  isAnimationActive={false}
                  // Recharts Bar defaults give a small gap between bins;
                  // setting maxBarSize wide + adjusting category gap makes
                  // adjacent weekend bars (Sat+Sun) read as one band.
                  barSize={9999}
                />
              </>
            )}
            <Tooltip
              // Recharts' TooltipContentProps is too narrow for the chart's
              // mixed dataKeys (string | number); typed loosely to match our
              // payload shape (each line we render contributes name/value/color).
              content={((props: unknown) => {
                const { active, payload, label } = (props as {
                  active?: boolean;
                  payload?: ReadonlyArray<{ name?: string | number; value?: string | number; color?: string }>;
                  label?: string | number;
                });
                if (!active || !payload || payload.length === 0) return null;
                const hoveredDate = String(label ?? '');
                const dateActions = showAiActions
                  ? actionDatesOnChart.find(a => a.date === hoveredDate)
                  : undefined;
                const dateUserEdits = showUserEdits
                  ? userEditDatesOnChart.find(u => u.date === hoveredDate)
                  : undefined;
                // Day-of-week badge in the tooltip header. Weekends use the
                // same slate palette as the weekend column shading so the
                // relationship is obvious; weekdays use a quieter neutral
                // gray so they don't compete with the day's actual data.
                const hoveredRow = chartData.find(d => d.date === hoveredDate) as { _weekendBg?: number; rawDate?: string } | undefined;
                const dowIdx = hoveredRow?.rawDate ? new Date(hoveredRow.rawDate).getDay() : null;
                const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const dowLabel = dowIdx != null ? DOW_LABELS[dowIdx] : null;
                const isWeekend = dowIdx === 0 || dowIdx === 6;
                return (
                  <div className="bg-white rounded-lg shadow-lg border border-gray-100 p-3 text-xs min-w-[180px]">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <p className="font-semibold text-gray-900">{hoveredDate}</p>
                      {dowLabel && (
                        <span className={cn(
                          'inline-flex items-center px-1.5 py-0 rounded-full text-[9px] font-medium uppercase tracking-wider',
                          isWeekend ? 'bg-slate-100 text-slate-600' : 'bg-gray-100 text-gray-500',
                        )}>
                          {dowLabel}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {payload
                        // Skip the hidden _weekendBg bar — it's a layout-only
                        // element, not a metric the user cares about.
                        .filter(p => String(p.name ?? '') !== '_weekendBg')
                        .map((p, idx: number) => {
                        const name = String(p.name ?? '');
                        const isPrev = name.startsWith('prev_') || name.toLowerCase().startsWith('previous ');
                        const metricKey = name.startsWith('prev_')
                          ? name.replace('prev_', '')
                          : METRIC_OPTIONS.find(m => m.label === name || m.value === name)?.value || name;
                        const metric = METRIC_OPTIONS.find(m => m.value === metricKey);
                        const isCurrency = metricKey === 'spend' || metricKey === 'cpc' || metricKey === 'cpa' || metricKey === 'conversionValue';
                        const v = Number(p.value) || 0;
                        const display = isCurrency ? fmt(v, cs) : metricKey === 'roas' ? `${v.toFixed(1)}x` : Math.round(v).toLocaleString();
                        return (
                          <div key={idx} className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-1.5 text-gray-700">
                              <div className={cn('w-2 h-2', isPrev ? 'rounded-full opacity-50' : 'rounded-sm')} style={{ background: p.color }} />
                              <span>{isPrev ? `Previous ${metric?.label || metricKey}` : (metric?.label || metricKey)}</span>
                            </div>
                            <span className="font-medium text-gray-900">{display}</span>
                          </div>
                        );
                      })}
                    </div>
                    {dateActions && dateActions.actions.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-1.5 text-amber-800 font-medium mb-1">
                          <div className="w-2 h-2 rounded-full bg-amber-500" />
                          <span>{dateActions.count} optimization{dateActions.count !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-0.5 text-gray-600">
                          {dateActions.actions.slice(0, 3).map((a, i: number) => {
                            // Pretty action-type label: ADD_NEGATIVE_KEYWORD → "Add negative keyword"
                            const prettyType = String(a.action_type)
                              .toLowerCase()
                              .replace(/_/g, ' ')
                              .replace(/^./, c => c.toUpperCase());
                            return (
                              <p key={i} className="truncate" title={prettyType}>
                                {prettyType}
                                {a.campaign_name ? ` · ${a.campaign_name}` : ''}
                              </p>
                            );
                          })}
                          {dateActions.actions.length > 3 && (
                            <p className="text-gray-400 italic">+ {dateActions.actions.length - 3} more</p>
                          )}
                        </div>
                      </div>
                    )}
                    {dateUserEdits && dateUserEdits.events.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-1.5 text-slate-700 font-medium mb-1">
                          <div className="w-2 h-2 rounded-full bg-slate-500" />
                          <span>{dateUserEdits.count} user edit{dateUserEdits.count !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-0.5 text-gray-600">
                          {dateUserEdits.events.slice(0, 3).map((e, i: number) => {
                            // Resource type prettified: CAMPAIGN_BUDGET → "Campaign budget"
                            const prettyResource = String(e.resource_type ?? 'change')
                              .toLowerCase()
                              .replace(/_/g, ' ')
                              .replace(/^./, c => c.toUpperCase());
                            const op = e.operation ? ` (${e.operation.toLowerCase()})` : '';
                            return (
                              <p key={i} className="truncate" title={`${prettyResource}${op} · ${e.actor || 'unknown'}`}>
                                {prettyResource}{op}
                                {e.actor ? ` · ${e.actor}` : ''}
                              </p>
                            );
                          })}
                          {dateUserEdits.events.length > 3 && (
                            <p className="text-gray-400 italic">+ {dateUserEdits.events.length - 3} more</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            />
            {/* Solid lines: one per selected metric, in the canonical metric
                order — the tooltip lists rows in Line render order, so this
                keeps the hover box in step with the tiles and legend. */}
            {METRIC_OPTIONS.filter(m => selectedMetrics.has(m.value)).map(({ value: metric }) => (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={METRIC_COLORS[metric] || '#290BD9'}
                strokeWidth={2.5}
                dot={false}
                name={METRIC_OPTIONS.find(m => m.value === metric)?.label || metric}
              />
            ))}
            {/* Dotted previous-period overlay — only when exactly one metric
                is being plotted (multi-line + dotted variants gets noisy). */}
            {comparisonMode !== 'none' && selectedMetrics.size === 1 && (
              <Line
                type="monotone"
                dataKey={`prev_${selectedMetric}`}
                stroke={METRIC_COLORS[selectedMetric] || '#290BD9'}
                strokeWidth={1.5}
                strokeDasharray="6 4"
                dot={false}
                name={`Previous ${METRIC_OPTIONS.find(m => m.value === selectedMetric)?.label || selectedMetric}`}
                opacity={0.5}
              />
            )}
            {showAiActions && actionDatesOnChart.map((action) => (
              <ReferenceDot key={`g-${action.date}`} x={action.date} y={action.value} r={5} fill="#d97706" stroke="#ffffff" strokeWidth={2}>
                <title>{`${action.count} optimization${action.count > 1 ? 's' : ''} on ${action.date}`}</title>
              </ReferenceDot>
            ))}
            {/* User-edit dots — slate fill, slightly smaller diamond-ish via
                square stroke contrast. Same Y-axis alignment to the primary
                metric as the optimization dots so they read on the same line. */}
            {showUserEdits && userEditDatesOnChart.map((edit) => (
              <ReferenceDot key={`u-${edit.date}`} x={edit.date} y={edit.value} r={4} fill="#64748b" stroke="#ffffff" strokeWidth={2}>
                <title>{`${edit.count} user edit${edit.count > 1 ? 's' : ''} on ${edit.date}`}</title>
              </ReferenceDot>
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex h-[280px] items-center justify-center">
          <EmptyChart icon={TrendingUp} message={emptyMessage ?? 'Time-series data will appear once campaigns have performance data'} />
        </div>
      )}
      {/* #4255 — Legend below chart. Each chip shows the metric colour
          and its label; clicking removes the line (with the same ≥1
          guarantee as the delta cards). When the dotted compare overlay
          is rendering, the legend also shows a "Previous" entry so users
          know what the dashed line represents. */}
      {chartData.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 mt-3 px-1 text-xs">
          {/* Chips follow the summary tiles' order so key and box always
              match; the Set's insertion order is not a display order. */}
          {(deltaCards.length > 0
            ? deltaCards.map(c => c.key).filter(k => selectedMetrics.has(k))
            : Array.from(selectedMetrics)
          ).map(metric => {
            const color = METRIC_COLORS[metric] || '#290BD9';
            const label = METRIC_OPTIONS.find(m => m.value === metric)?.label || metric;
            return (
              <button
                key={metric}
                type="button"
                onClick={() => toggleMetric(metric)}
                className="flex items-center gap-1.5 text-gray-700 hover:text-gray-900"
                title="Click to remove from chart"
              >
                <div className="w-3 h-0.5 rounded-sm" style={{ background: color }} />
                <span>{label}</span>
              </button>
            );
          })}
          {comparisonMode !== 'none' && selectedMetrics.size === 1 && (
            <div className="flex items-center gap-1.5 text-gray-500">
              <svg width="14" height="2" className="overflow-visible">
                <line x1="0" y1="1" x2="14" y2="1" stroke={METRIC_COLORS[selectedMetric] || '#290BD9'} strokeWidth="1.5" strokeDasharray="3 2" opacity="0.5" />
              </svg>
              <span>Previous {METRIC_OPTIONS.find(m => m.value === selectedMetric)?.label || selectedMetric}</span>
            </div>
          )}
          {showAiActions && actionDatesOnChart.length > 0 && (
            <div className="flex items-center gap-1.5 text-gray-500">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span>Optimizations</span>
            </div>
          )}
          {showUserEdits && userEditDatesOnChart.length > 0 && (
            <div className="flex items-center gap-1.5 text-gray-500">
              <div className="w-2 h-2 rounded-full bg-slate-500" />
              <span>User edits</span>
            </div>
          )}
        </div>
      )}
      {/* Delta summary cards — below the plot (ruling 09-08); they double as
          multi-metric toggles. Active state shows the metric is being
          plotted; the ring colour matches the line colour so users can map
          card → line. */}
      {deltaCards.length > 0 && (
        // Column count follows the card count: ROAS is axed on windows with
        // no tracked conversion values, and the row reflows without a gap.
        <div className="grid gap-2 mt-4" style={{ gridTemplateColumns: `repeat(${deltaCards.length}, minmax(0, 1fr))` }}>
          {deltaCards.map(card => {
            const isActive = selectedMetrics.has(card.key);
            const color = METRIC_COLORS[card.key] || '#290BD9';
            const formatVal = (v: number) => card.isCurrency ? fmt(v, cs) : card.key === 'roas' ? `${v.toFixed(1)}x` : v.toLocaleString(undefined, { maximumFractionDigits: 0 });
            return (
              <button
                key={card.key}
                onClick={() => toggleMetric(card.key)}
                aria-pressed={isActive}
                className={cn(
                  // flex-col + justify-start overrides the button element's
                  // native vertical centring: tiles with less content (CPA,
                  // ROAS) otherwise float their headings mid-card.
                  'relative flex flex-col items-stretch justify-start text-left p-2.5 pr-8 rounded-lg border transition-all cursor-pointer',
                  isActive ? 'bg-white' : 'border-gray-200 hover:border-gray-300',
                )}
                style={isActive ? {
                  borderColor: color,
                  boxShadow: `inset 0 0 0 1px ${color}33`,
                  backgroundColor: `${color}08`,
                } : undefined}
              >
                {/* The ring alone did not read as a toggle affordance: an
                    explicit check square says "this plots a line". Decorative
                    only (a real Checkbox is a button and cannot nest inside
                    the tile button); aria-pressed carries the state. */}
                <span
                  aria-hidden
                  className={cn(
                    'absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-sm border text-white',
                    !isActive && 'border-gray-300 bg-white',
                  )}
                  style={isActive ? { backgroundColor: color, borderColor: color } : undefined}
                >
                  {isActive && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                {/* No active-state dot: it shifted the heading sideways on
                    toggle. The coloured ring + tint (same line colour) carry
                    the active mapping, so headings sit identically in every
                    state. Titles hold one line at every width down to 1200px:
                    the "Total" prefix only renders where seven tiles leave
                    room for it, and the label itself never wraps. */}
                <p className="mb-0.5 text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {card.label.startsWith('Total ') ? (
                    <>
                      <span className="hidden min-[1440px]:inline">Total </span>
                      {card.label.slice('Total '.length)}
                    </>
                  ) : (
                    card.label
                  )}
                </p>
                <p
                  className={cn(card.small ? 'text-sm leading-6' : 'text-base', 'font-semibold', card.unmeasured ? 'text-gray-400' : 'text-gray-900')}
                  title={card.unmeasuredNote}
                >
                  {card.unmeasured ? '–' : (card.displayValue ?? formatVal(card.current))}
                </p>
                {card.subLines && card.subLines.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {card.subLines.map((line) => (
                      <p key={line.platform} className="flex items-center gap-1 text-[10px] text-gray-500">
                        <PlatformLogo platform={line.platform} className="h-2.5 w-2.5 shrink-0" />
                        <span>{line.text}</span>
                      </p>
                    ))}
                  </div>
                )}
                {card.hasComparison && (
                  <div className={`flex items-center gap-0.5 mt-0.5 text-xs ${card.improving ? 'text-green-600' : 'text-red-600'}`}>
                    {card.delta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    <span>{Math.abs(card.delta).toFixed(1)}%</span>
                  </div>
                )}
                {/* Visible disclosure affordance (ruling 09-09): a hidden
                    title attr was undiscoverable. The house SimpleTooltip
                    (grey-black) carries the note; clicks on the icon must
                    not toggle the tile's line. */}
                {card.tooltip && (
                  <SimpleTooltip content={card.tooltip} className="max-w-[240px]">
                    <span
                      aria-label="About this metric"
                      className="absolute bottom-2 right-2 text-gray-400 hover:text-gray-600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Info className="h-3.5 w-3.5" />
                    </span>
                  </SimpleTooltip>
                )}
              </button>
            );
          })}
        </div>
      )}
      {/* Data-caveat footnotes (only rendered when a caveat actually applies
          to the current window): quiet, at the card's bottom edge. */}
      {footnotes && footnotes.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {footnotes.map((note) => (
            <p key={note} className="text-[11px] leading-snug text-gray-400">{note}</p>
          ))}
        </div>
      )}
    </Card>
  );
}
